import { businessData as businessDataAtom } from 'atoms';
import Loader from 'common/Loaders/Loader';
import InnerPageLayout from 'pages/Business/Partials/InnerPageLayout';
import { useRecoilState } from 'recoil';
import styled from 'styled-components';
import { toasterNotification } from 'utils/toaster';

import { useAuth } from 'context/AuthContext';

import React, { useCallback, useEffect, useState } from 'react';

import AvailabilityPopup from './AvailabilityPopup';
import AvailabilityTable from './AvailabilityTable';
import EmptyAvailability from './EmptyAvailability';

const DataContainer = styled.div`
    background: ${(props) => props.theme.colorBackground};
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    flexgrow: 1;
`;

const AvailabilityHome = () => {
    const { token, loading: authLoading } = useAuth();
    const [business] = useRecoilState(businessDataAtom);
    const [openPopup, setOpenPopup] = useState(false);
    const [availabilityList, setAvailabilityList] = useState([]);
    const [selectedAvailability, setSelectedAvailability] = useState(null);
    const [loading, setLoading] = useState(false);

    const getAvailabilityList = useCallback(async () => {
        if (!business?.id) {
            setAvailabilityList([]);
            return;
        }
        if (authLoading) return;
        if (!token) {
            console.error('No authentication token found');
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(
                `${process.env.REACT_APP_SERVER_URL}/api/providers/business/${business.id}/availability`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            const result = await response.json().catch(() => ({}));

            if (!response.ok || result.error) {
                const errorMessage = result.error || 'Failed to fetch business availability';
                console.error('Error fetching business availability:', errorMessage);
                setAvailabilityList([]);
                return;
            }

            const data = result.success ? result.data : result;
            const list = Array.isArray(data) ? data : data ? [data] : [];
            setAvailabilityList(list);
        } catch (err) {
            console.error('Unexpected error fetching business availability:', err);
            setAvailabilityList([]);
        } finally {
            setLoading(false);
        }
    }, [business?.id, token, authLoading]);

    useEffect(() => {
        if (business?.id && !authLoading && token) {
            getAvailabilityList();
        }
    }, [business?.id, token, authLoading, getAvailabilityList]);

    const togglePopup = (params) => {
        if (params?.row) {
            setSelectedAvailability(params.row);
        } else {
            setSelectedAvailability(null);
        }
        setOpenPopup((prev) => !prev);
    };

    const handleDelete = async (id) => {
        if (!id) return;
        if (!token) {
            toasterNotification('error', '', 'Authentication error. Please try again.');
            return;
        }

        const confirmDelete = window.confirm('Are you sure you want to delete this availability?');
        if (!confirmDelete) return;

        try {
            const response = await fetch(
                `${process.env.REACT_APP_SERVER_URL}/api/providers/business/availability/${id}`,
                {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            const result = await response.json().catch(() => ({}));

            if (!response.ok || result.error) {
                const errorMessage = result.error || 'Failed to delete availability';
                toasterNotification('error', '', errorMessage);
                return;
            }

            toasterNotification('success', '', 'Availability deleted successfully.');
            await getAvailabilityList();
        } catch (err) {
            console.error('Error deleting business availability:', err);
            toasterNotification('error', '', 'An unexpected error occurred. Please try again.');
        }
    };

    return (
        <div className="h-full bg-[#F5F1ED]" style={{ display: 'flex', flexDirection: 'column', flex: 1, height: '100%' }}>
            {loading ? (
                <Loader />
            ) : (
                <>
                    {availabilityList?.length === 0 && (
                        <EmptyAvailability togglePopup={togglePopup} />
                    )}

                    {availabilityList?.length > 0 && (
                        <DataContainer>
                            <InnerPageLayout
                                heading="Availability"
                                btnText="Add Availability"
                                togglePopup={togglePopup}
                                buttonType="secondary"
                            >
                                <AvailabilityTable rows={availabilityList} togglePopup={togglePopup} />
                            </InnerPageLayout>
                        </DataContainer>
                    )}
                </>
            )}

            <AvailabilityPopup
                open={openPopup}
                onClose={togglePopup}
                businessId={business?.id}
                availabilityList={availabilityList}
                selectedAvailability={selectedAvailability}
                refreshList={getAvailabilityList}
                onDelete={handleDelete}
            />
        </div>
    );
};

export default AvailabilityHome;
